import type { ApiClient } from '@church/api-client'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '../api-context.tsx'
import { sessionKeys } from './keys.ts'

/**
 * The session bootstrap: user, memberships and grants from `GET /api/v1/me`.
 * Everything identity-shaped on both platforms hangs off this one query —
 * `useSession`, `usePermissions` and `useOrg` are views over its cache entry.
 *
 * A 401 is not an error here: it *is* the signed-out state.
 */

/**
 * The fetcher, exported standalone so route guards (TanStack Router
 * `beforeLoad`, Expo Router redirects) can `queryClient.fetchQuery` the same
 * cache entry the hook reads — one definition, no drift.
 */
export async function fetchMe(api: ApiClient) {
  const { data, error, status } = await api.api.v1.me.get()
  if (status === 401) return null
  if (error) throw error
  return data
}

export function useMe() {
  const api = useApi()

  return useQuery({
    queryKey: sessionKeys.me(),
    queryFn: () => fetchMe(api),
    staleTime: 60_000,
    retry: false,
  })
}

/** The resolved payload shape (null ⇒ signed out). */
export type Me = NonNullable<Awaited<ReturnType<typeof fetchMe>>>
